import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setStoredToken } from "@/lib/api";

const AuthContext = createContext({ user: null, loading: true, refresh: async () => {}, logout: async () => {} });

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 is the expected logged-out path; only log anything else.
      if (err?.response?.status !== 401) console.error("Auth refresh failed:", err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /auth/me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = async () => {
    try {
      await api.post("/auth/logout", {});
    } catch (err) {
      console.error("Logout request failed:", err);
    }
    // Clear the cross-domain Bearer fallback token too
    setStoredToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
